import type { RetryableFunction } from '../interfaces/RetryableFunction';

/**
 * @group Functions
 * @summary <blockquote>Marks an async function as retryable for use with withRetry.</blockquote>
 *
 * This wrapper function explicitly marks a function as a RetryableFunction,
 * signaling that it is designed to throw errors for retry logic rather than
 * returning Result types. Functions wrapped with retryable() are exempt from
 * the result-pattern ESLint rule.
 *
 * @example
 * ```typescript
 * import { withRetry, retryable } from '@shared/utils';
 *
 * const result = await withRetry(
 *   retryable(async () => {
 *     const response = await fetch(url);
 *     if (!response.ok) throw new Error(response.statusText);
 *     return response.json();
 *   }),
 *   { maxAttempts: 3 }
 * );
 * ```
 *
 * @typeParam T - The return type of the async function
 * @param fn - The async function that may throw errors for retry
 * @returns The same function typed as RetryableFunction<T>
 */
export function retryable<T>(fn: () => Promise<T>): RetryableFunction<T> {
  return fn;
}
